import { array } from 'fp-ts';
import { FormInputField, FormSelectionCardGroupField, FormTextareaField, lt } from '@mews/framework';
import { DividerList, ContentSection } from '@mews-commander/ui';
import { Banner, Stack } from '@optimus-web/core';
import { useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { DataTestId } from '@mews-commander/enums';
import { DetailsFieldName } from '../../../../../../types';
import { useIntersectingServicesSuspenseQuery } from '../../queries/intersectingServices';
import { useSelectedEnterpriseIdsContext } from '../../contexts/SelectedEnterpriseIdsContext';
import { ServiceSelectField } from './ServiceSelectField';

export const RateGroupFormDetailsFields = () => {
    const { enterpriseIds } = useSelectedEnterpriseIdsContext();
    const { noIntersectingServices } = useIntersectingServicesSuspenseQuery({ enterpriseIds });
    const { data } = useRepositorySuspenseQuery('portfolioCentralPaymentPolicies', 'getAll', {});
    const noPaymentPolicies = array.isEmpty(data.centralPaymentPolicies);

    return (
        <Stack spacing="400" vertical>
            {noPaymentPolicies || noIntersectingServices ? (
                <Stack spacing="200" vertical>
                    {noPaymentPolicies ? (
                        <Banner
                            variant="warning"
                            primaryText={lt('CentralRateGroupNoPoliciesMessageTitle')}
                            secondaryText={lt('CentralRateGroupNoPoliciesMessageDescription')}
                        />
                    ) : null}
                    {noIntersectingServices ? (
                        <Banner
                            variant="warning"
                            primaryText={lt('RateGroupServiceKeyNotExistTitle', { count: enterpriseIds.length })}
                            secondaryText={lt('RateGroupServiceKeyNotExistDescription', {
                                count: enterpriseIds.length,
                            })}
                        />
                    ) : null}
                </Stack>
            ) : null}

            <DividerList spacing="400">
                <ContentSection title={lt('Type')}>
                    <FormSelectionCardGroupField
                        name={DetailsFieldName.RateGroupType}
                        options={rateGroupTypeOptions}
                        templateColumns="1fr 1fr"
                        type="radio"
                        disabled={noIntersectingServices || noPaymentPolicies}
                    />
                </ContentSection>
                <ContentSection title={lt('DetailPlural')}>
                    <ServiceSelectField disabled={noPaymentPolicies} />
                    <FormInputField
                        name={DetailsFieldName.Name}
                        required
                        label={lt('Name')}
                        disabled={noIntersectingServices || noPaymentPolicies}
                    />
                    <FormInputField
                        name={DetailsFieldName.ShortName}
                        label={lt('ShortName')}
                        disabled={noIntersectingServices || noPaymentPolicies}
                    />
                    <FormTextareaField
                        name={DetailsFieldName.Description}
                        label={lt('Description')}
                        disabled={noIntersectingServices || noPaymentPolicies}
                    />
                    <FormInputField
                        label={lt('RateGroupKey')}
                        name={DetailsFieldName.RateGroupKey}
                        helper={lt('RateGroupKeyFieldHelper')}
                        disabled={noIntersectingServices || noPaymentPolicies}
                        required
                    />
                </ContentSection>
            </DividerList>
        </Stack>
    );
};

const rateGroupTypeOptions = [
    {
        dataTestId: DataTestId.RateGroupFormManageCentrallyButton,
        description: 'Creates a central rate group that affects selected properties.',
        label: 'Manage centrally',
        name: `${DetailsFieldName.RateGroupType}.central`,
        value: 'central',
    },
    {
        description: 'Creates independent rate groups for each property.',
        label: 'Manage locally',
        name: `${DetailsFieldName.RateGroupType}.local`,
        value: 'local',
    },
];
